import { getDefaultColorwayId } from '@apps/card/hooks/useColorways';
import type { PageJson } from '@apps/card/routes/CardCustomizer/components/PageJson.types';
import { withWindow } from '@shared/utils/withWindow';
import { useEffect, useState } from 'react';

// Deferred on the server too, so the markup it sends matches the client's first render.
let shouldDeferInitialRender = withWindow(browserWindow => !!browserWindow.__INITIAL_APOLLO_STATE__, true);

type UseSsrPreviewRenderDelayArgs = Readonly<{
  cardFront: PageJson;
  /** The design's static image: the one its gallery tile and `og:image` show. */
  thumbnailUrl: string | undefined;
  /** None means the card shows its default colorway. */
  selectedColorwayId: string | undefined;
}>;

/**
 * Because we can't load fonts when SSR, we need to delay the initial render of the preview.
 *
 * Meanwhile the design's static image stands in (`showsThumbnail`), so the card isn't blank. The
 * image only shows the default colorway, so any other selection keeps the empty card instead.
 */
export const useSsrPreviewRenderDelay = ({ cardFront, thumbnailUrl, selectedColorwayId }: UseSsrPreviewRenderDelayArgs) => {
  const [canRenderPreview, setCanRenderPreview] = useState(!shouldDeferInitialRender);

  useEffect(() => {
    if (!canRenderPreview) {
      setTimeout(() => {
        shouldDeferInitialRender = false;
        setCanRenderPreview(true);
      }, 500);
    }
  }, [canRenderPreview]);

  const isDefaultColorway = !selectedColorwayId || selectedColorwayId === getDefaultColorwayId(cardFront);
  const showsThumbnail = !canRenderPreview && !!thumbnailUrl && isDefaultColorway;

  return { canRenderPreview, showsThumbnail };
};
